import * as XLSX from 'xlsx';
import { normalizeAsRepairToken } from './asRepairCalculations';
import {
    applyAsRepairAutoValuesToForm,
    buildEmptyAsRepairFormValues,
    normalizeAsRepairFormConfig,
    parseQuantityValue,
} from './asRepairFormConfig';

/** 엑셀 열 고정 (1행부터 데이터, 헤더 없음) */
export const AS_REPAIR_EXCEL_COLUMN_MAPPING = [
    { column: 'A', index: 0, role: 'boxNumber', label: '박스 번호' },
    { column: 'B', index: 1, role: 'modelName', label: '모델명' },
    { column: 'C', index: 2, role: 'siteName', label: '현장명' },
    { column: 'D', index: 3, role: 'totalQty', label: '전체 수량' },
    { column: 'E', index: 4, role: 'shipDate', label: '출고일자' },
];

const EXCEL_ROLE_FORM_LABEL_HINTS = {
    boxNumber: ['박스 번호', '박스번호', '박스'],
    modelName: ['모델명', '모델', '품목코드'],
    siteName: ['현장명', '현장'],
    totalQty: ['전체 수량', '전체수량'],
    shipDate: ['출고일자', '출고일'],
};

const normFieldLabel = (label) => String(label || '').replace(/\s+/g, '');

const isOtherQtyFormField = (field) => {
    const nl = normalizeAsRepairToken(field.label);
    return (
        nl.includes('실입고')
        || nl.includes('진행중')
        || nl.includes('수리완료')
        || nl.includes('완료수량')
        || nl.includes('부족')
        || (nl.includes('입고') && !nl.includes('전체'))
    );
};

const pickFormFieldByRole = (fields, role) => {
    const hints = EXCEL_ROLE_FORM_LABEL_HINTS[role] || [];
    for (const hint of hints) {
        const h = normFieldLabel(hint);
        const found = fields.find((f) => {
            const label = normFieldLabel(f.label);
            return label === h || label.includes(h);
        });
        if (found) {
            return found;
        }
    }
    if (role === 'modelName') {
        return fields.find((f) => f.key === 'productName') || null;
    }
    if (role === 'siteName') {
        return fields.find((f) => f.key === 'siteName') || null;
    }
    if (role === 'totalQty') {
        const qtyFields = fields.filter(
            (f) => f.inputType === 'quantity' && !isOtherQtyFormField(f),
        );
        return qtyFields.find((f) => normalizeAsRepairToken(f.label).includes('전체')) || null;
    }
    if (role === 'shipDate') {
        return fields.find((f) => normalizeAsRepairToken(f.label).includes('출고')) || null;
    }
    return null;
};

/** 엑셀 A~E열 → A/S 설정 필드 */
export const getAsRepairExcelFieldMap = (config) => {
    const fields = normalizeAsRepairFormConfig(config).fields;
    const map = {};
    AS_REPAIR_EXCEL_COLUMN_MAPPING.forEach(({ role }) => {
        map[role] = pickFormFieldByRole(fields, role);
    });
    return map;
};

const cellTextAsIs = (value) => {
    if (value == null || value === '') {
        return '';
    }
    return String(value);
};

const getCellRaw = (sheet, rowIndex, colIndex) => {
    const addr = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
    const cell = sheet[addr];
    if (!cell) {
        return '';
    }
    if (cell.w != null && String(cell.w).trim() !== '') {
        return cell.w;
    }
    if (cell.v == null) {
        return '';
    }
    return cellTextAsIs(cell.v);
};

const setExcelFieldValue = (values, field, raw, role = null) => {
    if (!field) {
        return;
    }
    if (role === 'shipDate') {
        values[field.key] = cellTextAsIs(raw);
        return;
    }
    if (field.inputType === 'quantity') {
        if (raw === '' || raw == null) {
            values[field.key] = '';
            return;
        }
        values[field.key] = parseQuantityValue(raw);
        return;
    }
    values[field.key] = cellTextAsIs(raw);
};

const rowHasAnyMappedData = (sheet, rowIndex) => (
    AS_REPAIR_EXCEL_COLUMN_MAPPING.some(({ index }) => (
        String(getCellRaw(sheet, rowIndex, index)).trim() !== ''
    ))
);

/**
 * XLSX / XLSB → A/S form values 배열 (A~E열 고정)
 * @returns {{ rows: object[], skipped: number, errors: string[] }}
 */
export const parseAsRepairExcelFile = async (file, config) => {
    const errors = [];
    if (!file) {
        return { rows: [], skipped: 0, errors: ['파일이 없습니다.'] };
    }

    const ext = String(file.name || '').split('.').pop()?.toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xlsb') {
        return { rows: [], skipped: 0, errors: ['XLSX, XLSB 파일만 업로드할 수 있습니다.'] };
    }

    const formConfig = normalizeAsRepairFormConfig(config);
    const fields = formConfig.fields;
    const fieldMap = getAsRepairExcelFieldMap(formConfig);

    if (!fieldMap.modelName) {
        return {
            rows: [],
            skipped: 0,
            errors: ['A/S 리스트 설정에서 모델명 항목을 찾을 수 없습니다.'],
        };
    }

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
    if (!workbook.SheetNames.length) {
        return { rows: [], skipped: 0, errors: ['엑셀 시트가 비어 있습니다.'] };
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const ref = sheet['!ref'];
    if (!ref) {
        return { rows: [], skipped: 0, errors: ['엑셀에 데이터가 없습니다.'] };
    }

    const range = XLSX.utils.decode_range(ref);
    const rows = [];
    let skipped = 0;

    for (let r = range.s.r; r <= range.e.r; r += 1) {
        if (!rowHasAnyMappedData(sheet, r)) {
            skipped += 1;
            continue;
        }

        const values = buildEmptyAsRepairFormValues(formConfig);
        AS_REPAIR_EXCEL_COLUMN_MAPPING.forEach(({ index, role }) => {
            const field = fieldMap[role];
            if (!field) {
                return;
            }
            const raw = getCellRaw(sheet, r, index);
            setExcelFieldValue(values, field, raw, role);
        });

        try {
            rows.push(applyAsRepairAutoValuesToForm(fields, values));
        } catch {
            rows.push(values);
        }
    }

    if (rows.length === 0) {
        errors.push('등록할 데이터 행이 없습니다.');
    }

    return { rows, skipped, errors };
};
